import React, { useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";
import { themevals } from "theme/themevals";
import { useToast } from "components/toast";
import { setQuotePaymentTerms } from "api/Quotes";

/**
 * Staff control on a quote: require a deposit before scheduling, and/or turn
 * the early-payment savings off for this one quote (Jack, 2026-10-01). A
 * required deposit always turns the savings off. The quote PDF, the in-app
 * quote, the public quote page, the deposit-on-accept gate and the quote's
 * invoice all read these.
 */
export default function QuotePaymentTerms({ quote, onSaved }: { quote: any; onSaved: () => void | Promise<void> }) {
  const toast = useToast();
  const [requiresDeposit, setRequiresDeposit] = useState<boolean>(!!quote?.requiresDeposit);
  const [pct, setPct] = useState<string>(String(quote?.depositPercentage ?? 50));
  const [discountOff, setDiscountOff] = useState<boolean>(!!quote?.earlyPaymentDiscountOff);
  const [hideNote, setHideNote] = useState<boolean>(!!quote?.hideClientPricingNote);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRequiresDeposit(!!quote?.requiresDeposit);
    setPct(String(quote?.depositPercentage ?? 50));
    setDiscountOff(!!quote?.earlyPaymentDiscountOff);
    setHideNote(!!quote?.hideClientPricingNote);
  }, [quote?.id, quote?.requiresDeposit, quote?.depositPercentage, quote?.earlyPaymentDiscountOff, quote?.hideClientPricingNote]);

  const dirty =
    requiresDeposit !== !!quote?.requiresDeposit ||
    (requiresDeposit && Number(pct) !== Number(quote?.depositPercentage ?? 50)) ||
    (!requiresDeposit && discountOff !== !!quote?.earlyPaymentDiscountOff) ||
    hideNote !== !!quote?.hideClientPricingNote;

  const save = async () => {
    const n = Number(pct);
    if (requiresDeposit && !(n > 0 && n < 100)) {
      toast.add("Deposit must be between 1% and 99%.", "danger");
      return;
    }
    setBusy(true);
    const res = await setQuotePaymentTerms(quote.id, {
      requiresDeposit,
      depositPercentage: requiresDeposit ? Math.round(n) : null,
      earlyPaymentDiscountOff: requiresDeposit || discountOff,
      hideClientPricingNote: hideNote,
    });
    setBusy(false);
    if (res?.error) {
      toast.add(res.error, "danger");
      return;
    }
    toast.add("Payment terms saved.", "success");
    await onSaved();
  };

  // Inline overrides on purpose: the app's global form CSS uppercases every
  // <label> and stretches every <input> to full width, which turned these
  // checkboxes into wide gaps with stacked capital text (2026-10-01).
  const label: React.CSSProperties = {
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    width: "auto",
    margin: 0,
    padding: 0,
    fontFamily: "Poppins",
    fontSize: 13,
    fontWeight: 400,
    lineHeight: "20px",
    letterSpacing: "normal",
    textTransform: "none",
    color: themevals.colors.black,
    cursor: "pointer",
    whiteSpace: "nowrap",
  };
  const box: React.CSSProperties = {
    width: 16,
    height: 16,
    minWidth: 16,
    flex: "0 0 16px",
    margin: 0,
    padding: 0,
    cursor: "pointer",
  };

  return (
    <Box
      sx={{
        width: "100%",
        marginTop: "24px",
        padding: "14px 16px",
        border: `1px solid ${themevals.colors.inputOutline}`,
        borderRadius: "8px",
        backgroundColor: "#FAFAF7",
      }}
    >
      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>Payment terms</Box>
      <Flex sx={{ alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "8px" }}>
        <label style={label}>
          <input type="checkbox" style={box} checked={requiresDeposit} onChange={(e) => setRequiresDeposit(e.target.checked)} />
          Require a deposit to schedule
        </label>
        {requiresDeposit && (
          <label style={label}>
            <input
              type="number"
              min={1}
              max={99}
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              style={{ width: 64, minWidth: 64, flex: "0 0 64px", margin: 0, padding: "4px 6px", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: 4, fontFamily: "Poppins", fontSize: 13 }}
            />
            %
          </label>
        )}
      </Flex>
      <label style={{ ...label, opacity: requiresDeposit ? 0.55 : 1 }}>
        <input
          type="checkbox"
          style={box}
          checked={requiresDeposit || discountOff}
          disabled={requiresDeposit}
          onChange={(e) => setDiscountOff(e.target.checked)}
        />
        No early-payment savings on this quote{requiresDeposit ? " (always off with a deposit)" : ""}
      </label>
      <div style={{ height: 6 }} />
      <label style={label}>
        <input type="checkbox" style={box} checked={hideNote} onChange={(e) => setHideNote(e.target.checked)} />
        Hide the &ldquo;registered client pricing is lower&rdquo; note (lead quote page)
      </label>
      <Flex sx={{ marginTop: "10px", gap: "10px", alignItems: "center" }}>
        <button
          type="button"
          disabled={!dirty || busy}
          onClick={save}
          style={{
            padding: "6px 14px",
            borderRadius: 6,
            border: "none",
            backgroundColor: !dirty || busy ? "#BFBFBF" : "#000",
            color: "#fff",
            fontFamily: "Poppins",
            fontSize: 13,
            cursor: !dirty || busy ? "default" : "pointer",
          }}
        >
          {busy ? "Saving\u2026" : "Save"}
        </button>
        <span style={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>
          Applies to this quote, its PDF and public page, and its invoice.
        </span>
      </Flex>
    </Box>
  );
}
